import React, { useState } from 'react';
import { reviewLabel } from '../../pages/fundingInboxModel';

// Josh 10/28: "i perposfully added a failsafe that staff review would be required if certain questions were
// answered certainly. it did its job, but i need it to allow me to review it." The answers are shown in place —
// on the inbox row and on the deal's Confirmation tab — with the answers that tripped the failsafe flagged, and
// the staff decision is recorded right here. Reviewing never funds a deal or changes its ledger.
const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

export default function DisclosureReview({ record, onDecide, busy }) {
  const [note, setNote] = useState('');
  const disclosures = record?.disclosures || [];
  const review = record?.review;
  const flaggedCount = disclosures.filter((d) => d.flagged).length;
  const canDecide = !!onDecide && !!record?.requires_review && !review;
  const verification = record?.verification;
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-4 text-sm space-y-3" data-testid={`disclosure-review-${record?.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-semibold text-slate-900">Business disclosures{record?.signer_name ? ` · answered by ${record.signer_name}` : ''}</p>
        <p className="text-xs text-slate-600">
          {record?.completed_at ? `Signed ${fmt(record.completed_at)}` : ''}
          {verification?.channel ? ` · verified by ${verification.channel === 'sms' ? 'text' : 'email'} (${verification.destination_masked || ''})` : ''}
        </p>
      </div>
      {flaggedCount > 0 && !review && (
        <p className="rounded bg-amber-100 px-3 py-2 text-amber-900" data-testid="review-reason">
          {flaggedCount === 1 ? 'One answer' : `${flaggedCount} answers`} tripped the staff-review failsafe. Review before funding.
        </p>
      )}
      {disclosures.length === 0 ? <p className="text-slate-500">No disclosure answers on this record.</p> : (
        <ol className="space-y-2">
          {disclosures.map((d, i) => (
            <li key={d.id} className={`flex items-start justify-between gap-4 rounded px-3 py-2 ${d.flagged ? 'bg-red-50 ring-1 ring-red-200' : 'bg-white'}`} data-testid={`disclosure-${d.id}`}>
              <span className="text-slate-800"><span className="mr-2 text-slate-400">{i + 1}.</span>{d.text}</span>
              <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold ${d.flagged ? 'bg-red-600 text-white' : 'bg-slate-100 text-slate-700'}`}>
                {d.answer === true ? 'Yes' : d.answer === false ? 'No' : '—'}{d.flagged ? ' · flagged' : ''}
              </span>
            </li>
          ))}
        </ol>
      )}
      {review ? (
        <p className={`rounded px-3 py-2 ${review.decision === 'cleared' ? 'bg-emerald-100 text-emerald-900' : 'bg-red-100 text-red-900'}`} data-testid="review-decision">
          <b>{reviewLabel(review)}</b> by {review.by_name || review.by} on {fmt(review.at)}{review.note ? ` — ${review.note}` : ''}
        </p>
      ) : canDecide && (
        <div className="space-y-2">
          <label className="block text-xs text-slate-700">Review note (optional)
            <textarea className="mt-1 w-full rounded border p-2 text-sm" rows={2} maxLength={1500} value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="What you checked, who you spoke to, why this is fine (or not)…" data-testid="review-note" />
          </label>
          <div className="flex flex-wrap gap-2">
            <button type="button" disabled={busy} onClick={() => onDecide('cleared', note)} className="rounded bg-emerald-700 px-3 py-2 text-sm text-white disabled:opacity-50" data-testid="review-clear">Reviewed — clear for funding</button>
            <button type="button" disabled={busy} onClick={() => onDecide('declined', note)} className="rounded bg-red-700 px-3 py-2 text-sm text-white disabled:opacity-50" data-testid="review-decline">Reviewed — do not fund</button>
          </div>
          <p className="text-xs text-slate-500">Your decision is signed into the confirmation's event log. It does not fund the deal or change its ledger.</p>
        </div>
      )}
    </div>
  );
}
